import { uniqueId } from 'lodash';
import { insert, insertAll, remove, update } from 'ramda';
import React, { useCallback } from 'react';

import { CategoryInputField } from '../CategoryInputField';
import { UNIQUEID_CATEGORY_PREFIX } from '../constants';

type SubcategoryInputFieldProps = {
  addTouchedId: (rowId: string) => void;
  canDelete: boolean;
  subcategoryIndex: number;
  subcategories: {
    name: string;
    _rowId: string;
  }[];
  fieldName: string;
  placeholder: string;
  removeTouchedRowId: (rowId: string) => void;
  rowId: string;
  touched: boolean;
  onChange: (
    subcategories: {
      name: string;
      _rowId: string;
    }[],
  ) => void;
};

const buildSubcategoryValue = (name: string) => ({ name, _rowId: uniqueId(UNIQUEID_CATEGORY_PREFIX) });

export const SubcategoryInputField = ({
  addTouchedId,
  canDelete,
  subcategoryIndex,
  subcategories,
  fieldName,
  placeholder,
  removeTouchedRowId,
  rowId,
  touched,
  onChange,
}: SubcategoryInputFieldProps) => {
  const handleBlur = useCallback(() => {
    addTouchedId(rowId);
  }, [addTouchedId, rowId]);

  const handlePaste = useCallback(
    (names: string[]) => {
      const { _rowId: currentInputRowId } = subcategories[subcategoryIndex];
      const [firstName, ...otherNames] = names;

      onChange(
        insertAll(
          subcategoryIndex + 1,
          otherNames.map(buildSubcategoryValue),
          update(subcategoryIndex, { name: firstName, _rowId: currentInputRowId }, subcategories),
        ),
      );
    },
    [onChange, subcategories, subcategoryIndex],
  );
  const handlePressEnter = useCallback(() => {
    onChange(insert(subcategoryIndex + 1, buildSubcategoryValue(''), subcategories));
  }, [onChange, subcategoryIndex, subcategories]);

  const handleRemove = useCallback(() => {
    removeTouchedRowId(rowId);
    onChange(remove(subcategoryIndex, 1, subcategories));
  }, [onChange, subcategoryIndex, subcategories, removeTouchedRowId, rowId]);

  return (
    <CategoryInputField
      canDelete={canDelete}
      fieldName={fieldName}
      onBlur={handleBlur}
      onPaste={handlePaste}
      onPressEnter={handlePressEnter}
      onRemove={handleRemove}
      placeholder={placeholder}
      touched={touched}
    />
  );
};
